import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  LayoutDashboard,
  Table2,
  Edit,
  Copy,
  Trash2,
  Eye,
  BarChart3,
  Download,
  Upload,
  FileJson,
  CheckSquare,
  Square,
  X,
  LineChart,
  PieChart,
  TrendingUp
} from 'lucide-react';

const VisualizationStudio = () => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  const navigate = useNavigate();

  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedComponents, setSelectedComponents] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);

  useEffect(() => {
    loadComponents();
  }, []);

  const loadComponents = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      console.log('🔍 Loading visualization components for tenant:', tenantCode);

      const response = await fetch('/api/visualization-components', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Failed to load components:', response.status, errorText);
        
        if (response.status === 500 && errorText.includes('Invalid object name')) {
          toast.error('Database tables not created. Please run the migration first.');
          throw new Error('Database tables not found. Run migration: backend/db/migrations/create-visualization-system-tables.sql');
        }
        
        throw new Error(`Failed to load components: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Loaded components:', data.data?.length || 0);
      setComponents(data.data || []);
      
      if (!data.data || data.data.length === 0) {
        console.log('ℹ️ No components found. Create your first visualization!');
      }
    } catch (error) {
      console.error('Error loading components:', error);
      toast.error(error.message || 'Failed to load visualization components');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = (type) => {
    navigate(`/visualization-studio/builder?type=${type}`);
  };

  const handleEdit = (id) => {
    navigate(`/visualization-studio/builder/${id}`);
  };

  const handleDuplicate = async (id) => {
    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/visualization-components/${id}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate component');
      }

      toast.success('Component duplicated successfully');
      loadComponents();
    } catch (error) {
      console.error('Error duplicating component:', error);
      toast.error('Failed to duplicate component');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this visualization component?')) {
      return;
    }

    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/visualization-components/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete component');
      }

      toast.success('Component deleted successfully');
      loadComponents();
    } catch (error) {
      console.error('Error deleting component:', error);
      toast.error('Failed to delete component');
    }
  };

  const handleExport = async (component) => {
    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      // Fetch full component details
      const response = await fetch(`/api/visualization-components/${component.ComponentID}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch component details');
      }

      const data = await response.json();
      const componentData = data.data || data;

      // Prepare export data (remove internal IDs and tenant-specific data)
      const exportData = {
        version: '1.0.0',
        exportedAt: new Date().toISOString(),
        component: {
          Name: componentData.Name,
          Description: componentData.Description || '',
          ComponentType: componentData.ComponentType,
          EntityName: componentData.EntityName,
          Configuration: componentData.config || componentData.ConfigJSON ? 
            (typeof componentData.config === 'string' ? JSON.parse(componentData.config) : componentData.config) :
            (componentData.ConfigJSON ? (typeof componentData.ConfigJSON === 'string' ? JSON.parse(componentData.ConfigJSON) : componentData.ConfigJSON) : null),
          IsActive: componentData.IsActive !== undefined ? componentData.IsActive : true
        }
      };

      // Create and download JSON file
      const jsonStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${componentData.Name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_component.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Component exported successfully');
    } catch (error) {
      console.error('Error exporting component:', error);
      toast.error('Failed to export component');
    }
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const text = await file.text();
        const importData = JSON.parse(text);

        const tenantCode = getProtectedTenantCode();
        const token = localStorage.getItem('token');

        // Check if it's a bulk import (multiple components)
        if (importData.components && Array.isArray(importData.components)) {
          // Bulk import with selection
          const components = importData.components;
          if (components.length === 0) {
            throw new Error('No components found in file');
          }

          // Show selection dialog
          const selectedForImport = await showImportSelectionDialog(components);
          if (!selectedForImport || selectedForImport.length === 0) {
            return;
          }

          let successCount = 0;
          let errorCount = 0;
          const errors = [];

          for (const component of selectedForImport) {
            try {
              if (!component.Name || !component.ComponentType || !component.EntityName) {
                throw new Error(`Missing required fields: ${component.Name || 'Unknown'}`);
              }

              const response = await fetch('/api/visualization-components', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                  'X-Tenant-Code': tenantCode
                },
                body: JSON.stringify({
                  componentType: component.ComponentType,
                  name: component.Name,
                  description: component.Description || '',
                  entityName: component.EntityName,
                  config: component.Configuration || {}
                })
              });

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                throw new Error(errorData.error || 'Failed to import component');
              }

              successCount++;
            } catch (error) {
              errorCount++;
              errors.push(`${component.Name || 'Unknown'}: ${error.message}`);
            }
          }

          if (successCount > 0) {
            toast.success(`Imported ${successCount} component(s) successfully${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
            if (errors.length > 0) {
              console.error('Import errors:', errors);
            }
            loadComponents();
          } else {
            throw new Error(`Failed to import all components. Errors: ${errors.join('; ')}`);
          }
        } else if (importData.component) {
          // Single component import
          const component = importData.component;
          if (!component.Name || !component.ComponentType || !component.EntityName) {
            throw new Error('Invalid file format: missing required fields (Name, ComponentType, EntityName)');
          }

          // Ask user to confirm import
          const confirmMessage = `Import component "${component.Name}"?\n\nType: ${component.ComponentType}\nEntity: ${component.EntityName}\n\nThis will create a new component.`;
          if (!window.confirm(confirmMessage)) {
            return;
          }

          // Create new component from imported data
          const response = await fetch('/api/visualization-components', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'X-Tenant-Code': tenantCode
            },
            body: JSON.stringify({
              componentType: component.ComponentType,
              name: component.Name,
              description: component.Description || '',
              entityName: component.EntityName,
              config: component.Configuration || {}
            })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(errorData.error || 'Failed to import component');
          }

          toast.success('Component imported successfully');
          loadComponents();
        } else {
          throw new Error('Invalid file format: missing component or components array');
        }
      } catch (error) {
        console.error('Error importing component:', error);
        toast.error(error.message || 'Failed to import component. Please check the file format.');
      }
    };
    input.click();
  };

  const showImportSelectionDialog = (components) => {
    return new Promise((resolve) => {
      // Create modal overlay
      const overlay = document.createElement('div');
      overlay.className = 'fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4';
      overlay.style.zIndex = '9999';

      // Create modal content
      const modal = document.createElement('div');
      modal.className = 'bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col';
      
      const selectedSet = new Set();
      
      const renderModal = () => {
        modal.innerHTML = `
          <div class="p-6 border-b border-gray-200 dark:border-gray-700">
            <div class="flex items-center justify-between">
              <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">Select Components to Import</h2>
              <button class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" id="close-import-modal">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
              ${selectedSet.size} of ${components.length} selected
            </p>
          </div>
          <div class="flex-1 overflow-y-auto p-6">
            <div class="mb-4 flex items-center justify-between">
              <button 
                id="select-all-import" 
                class="text-sm text-teal-600 dark:text-teal-400 hover:underline"
              >
                ${selectedSet.size === components.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div class="space-y-2">
              ${components.map((comp, index) => `
                <label class="flex items-start p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                  <input 
                    type="checkbox" 
                    class="mt-1 mr-3 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    data-index="${index}"
                    ${selectedSet.has(index) ? 'checked' : ''}
                  />
                  <div class="flex-1">
                    <div class="font-medium text-gray-900 dark:text-gray-100">${comp.Name || 'Unnamed Component'}</div>
                    <div class="text-sm text-gray-500 dark:text-gray-400">
                      Type: ${comp.ComponentType || 'Unknown'} | Entity: ${comp.EntityName || 'Unknown'}
                    </div>
                    ${comp.Description ? `<div class="text-xs text-gray-400 dark:text-gray-500 mt-1">${comp.Description}</div>` : ''}
                  </div>
                </label>
              `).join('')}
            </div>
          </div>
          <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
            <button 
              id="cancel-import" 
              class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button 
              id="confirm-import" 
              class="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              ${selectedSet.size === 0 ? 'disabled' : ''}
            >
              Import ${selectedSet.size} Component(s)
            </button>
          </div>
        `;

        // Attach event listeners
        const checkboxes = modal.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(cb => {
          cb.addEventListener('change', (e) => {
            const index = parseInt(e.target.dataset.index);
            if (e.target.checked) {
              selectedSet.add(index);
            } else {
              selectedSet.delete(index);
            }
            renderModal();
          });
        });

        modal.querySelector('#select-all-import')?.addEventListener('click', () => {
          if (selectedSet.size === components.length) {
            selectedSet.clear();
          } else {
            components.forEach((_, index) => selectedSet.add(index));
          }
          renderModal();
        });

        modal.querySelector('#close-import-modal')?.addEventListener('click', () => {
          document.body.removeChild(overlay);
          resolve(null);
        });

        modal.querySelector('#cancel-import')?.addEventListener('click', () => {
          document.body.removeChild(overlay);
          resolve(null);
        });

        modal.querySelector('#confirm-import')?.addEventListener('click', () => {
          const selected = Array.from(selectedSet).map(i => components[i]);
          document.body.removeChild(overlay);
          resolve(selected);
        });

        // Close on overlay click
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            document.body.removeChild(overlay);
            resolve(null);
          }
        });
      };

      overlay.appendChild(modal);
      document.body.appendChild(overlay);
      renderModal();
    });
  };

  const toggleSelection = (componentId) => {
    const newSelection = new Set(selectedComponents);
    if (newSelection.has(componentId)) {
      newSelection.delete(componentId);
    } else {
      newSelection.add(componentId);
    }
    setSelectedComponents(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedComponents.size === filteredComponents.length) {
      setSelectedComponents(new Set());
    } else {
      setSelectedComponents(new Set(filteredComponents.map(c => c.ComponentID)));
    }
  };

  const clearSelection = () => {
    setSelectedComponents(new Set());
    setSelectionMode(false);
  };

  const handleBulkExport = async () => {
    // Determine which components to export
    const componentsToExport = selectionMode && selectedComponents.size > 0
      ? filteredComponents.filter(c => selectedComponents.has(c.ComponentID))
      : filteredComponents;

    if (componentsToExport.length === 0) {
      toast.error('No components selected to export');
      return;
    }

    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      // Fetch all component details
      const componentPromises = componentsToExport.map(async (comp) => {
        const response = await fetch(`/api/visualization-components/${comp.ComponentID}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-Code': tenantCode
          }
        });
        if (!response.ok) throw new Error(`Failed to fetch ${comp.Name}`);
        const data = await response.json();
        return data.data || data;
      });

      const componentsData = await Promise.all(componentPromises);

      // Prepare export data
      const exportData = {
        version: '1.0.0',
        exportedAt: new Date().toISOString(),
        count: componentsData.length,
        components: componentsData.map(comp => ({
          Name: comp.Name,
          Description: comp.Description || '',
          ComponentType: comp.ComponentType,
          EntityName: comp.EntityName,
          Configuration: comp.config || comp.ConfigJSON ? 
            (typeof comp.config === 'string' ? JSON.parse(comp.config) : comp.config) :
            (comp.ConfigJSON ? (typeof comp.ConfigJSON === 'string' ? JSON.parse(comp.ConfigJSON) : comp.ConfigJSON) : null),
          IsActive: comp.IsActive !== undefined ? comp.IsActive : true
        }))
      };

      // Create and download JSON file
      const jsonStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `visualization_components_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${componentsData.length} component(s) successfully`);
      
      // Clear selection after export if in selection mode
      if (selectionMode) {
        clearSelection();
      }
    } catch (error) {
      console.error('Error exporting components:', error);
      toast.error('Failed to export components');
    }
  };

  const filteredComponents = components.filter(comp => {
    const matchesSearch = comp.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         comp.Description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || comp.ComponentType === filterType;
    return matchesSearch && matchesType;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'StatCard':
        return <LayoutDashboard className="w-5 h-5" />;
      case 'Table':
        return <Table2 className="w-5 h-5" />;
      case 'BarChart':
        return <BarChart3 className="w-5 h-5" />;
      case 'LineChart':
        return <LineChart className="w-5 h-5" />;
      case 'PieChart':
        return <PieChart className="w-5 h-5" />;
      default:
        return <BarChart3 className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading components...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-md px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold mb-2">Visualization Studio</h1>
          <p className="text-teal-100">Create and manage reusable visualization components</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Actions Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search components..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              >
                <option value="all">All Types</option>
                <option value="StatCard">Stat Cards</option>
                <option value="Table">Tables</option>
                <option value="BarChart">Bar Charts</option>
                <option value="LineChart">Line Charts</option>
                <option value="PieChart">Pie Charts</option>
              </select>

              <div className="flex gap-2">
                {selectionMode ? (
                  <>
                    <button
                      onClick={toggleSelectAll}
                      className="btn btn-secondary flex items-center gap-2"
                      title={selectedComponents.size === filteredComponents.length ? 'Deselect All' : 'Select All'}
                    >
                      {selectedComponents.size === filteredComponents.length ? (
                        <CheckSquare className="w-4 h-4" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                      {selectedComponents.size === filteredComponents.length ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedComponents.size > 0 && (
                      <button
                        onClick={handleBulkExport}
                        className="btn btn-primary flex items-center gap-2"
                        title={`Export ${selectedComponents.size} selected component(s)`}
                      >
                        <Download className="w-4 h-4" />
                        Export Selected ({selectedComponents.size})
                      </button>
                    )}
                    <button
                      onClick={clearSelection}
                      className="btn btn-secondary flex items-center gap-2"
                      title="Cancel selection"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleImport}
                      className="btn btn-secondary flex items-center gap-2"
                      title="Import component from JSON file"
                    >
                      <Upload className="w-4 h-4" />
                      Import
                    </button>
                    {filteredComponents.length > 0 && (
                      <>
                        <button
                          onClick={() => setSelectionMode(true)}
                          className="btn btn-secondary flex items-center gap-2"
                          title="Select components to export"
                        >
                          <CheckSquare className="w-4 h-4" />
                          Select
                        </button>
                        <button
                          onClick={handleBulkExport}
                          className="btn btn-secondary flex items-center gap-2"
                          title="Export all filtered components"
                        >
                          <Download className="w-4 h-4" />
                          Export All
                        </button>
                      </>
                    )}
                  </>
                )}
                <button
                  onClick={() => handleCreate('StatCard')}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Stat Card
                </button>
                <button
                  onClick={() => handleCreate('Table')}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Table
                </button>
                <div className="flex gap-2 border-l border-gray-300 dark:border-gray-600 pl-2 ml-2">
                  <button
                    onClick={() => handleCreate('BarChart')}
                    className="btn btn-secondary flex items-center gap-2"
                    title="Create Bar Chart"
                  >
                    <BarChart3 className="w-4 h-4" />
                    Bar
                  </button>
                  <button
                    onClick={() => handleCreate('LineChart')}
                    className="btn btn-secondary flex items-center gap-2"
                    title="Create Line Chart"
                  >
                    <LineChart className="w-4 h-4" />
                    Line
                  </button>
                  <button
                    onClick={() => handleCreate('PieChart')}
                    className="btn btn-secondary flex items-center gap-2"
                    title="Create Pie Chart"
                  >
                    <PieChart className="w-4 h-4" />
                    Pie
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Components Grid */}
        {filteredComponents.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
            <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No visualization components yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Create your first visualization component to get started
            </p>
            <div className="flex justify-center gap-3 flex-wrap">
              <button
                onClick={() => handleCreate('StatCard')}
                className="btn btn-primary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Stat Card
              </button>
              <button
                onClick={() => handleCreate('Table')}
                className="btn btn-secondary flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Table
              </button>
              <button
                onClick={() => handleCreate('BarChart')}
                className="btn btn-secondary flex items-center gap-2"
              >
                <BarChart3 className="w-4 h-4" />
                Create Bar Chart
              </button>
              <button
                onClick={() => handleCreate('LineChart')}
                className="btn btn-secondary flex items-center gap-2"
              >
                <LineChart className="w-4 h-4" />
                Create Line Chart
              </button>
              <button
                onClick={() => handleCreate('PieChart')}
                className="btn btn-secondary flex items-center gap-2"
              >
                <PieChart className="w-4 h-4" />
                Create Pie Chart
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredComponents.map(component => {
              const isSelected = selectedComponents.has(component.ComponentID);
              return (
              <div
                key={component.ComponentID}
                className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-all p-6 ${
                  selectionMode ? 'cursor-pointer' : ''
                } ${
                  isSelected ? 'ring-2 ring-teal-500 bg-teal-50 dark:bg-teal-900/20' : ''
                }`}
                onClick={selectionMode ? () => toggleSelection(component.ComponentID) : undefined}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 flex-1">
                    {selectionMode && (
                      <div className="mt-1">
                        {isSelected ? (
                          <CheckSquare className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    )}
                    <div className="p-2 bg-teal-100 dark:bg-teal-900/20 rounded-lg text-teal-600 dark:text-teal-400">
                      {getTypeIcon(component.ComponentType)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                        {component.Name}
                      </h3>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {component.ComponentType}
                      </span>
                    </div>
                  </div>
                </div>

                {component.Description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                    {component.Description}
                  </p>
                )}

                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                  <span>Entity: {component.EntityName}</span>
                  <span>By: {component.CreatedByName}</span>
                </div>

                {!selectionMode && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(component.ComponentID);
                      }}
                      className="flex-1 btn btn-secondary flex items-center justify-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExport(component);
                      }}
                      className="btn btn-secondary p-2"
                      title="Export to JSON"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(component.ComponentID);
                      }}
                      className="btn btn-secondary p-2"
                      title="Duplicate"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(component.ComponentID);
                      }}
                      className="btn btn-secondary p-2 text-red-600 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {selectionMode && isSelected && (
                  <div className="mt-2 text-sm text-teal-600 dark:text-teal-400 font-medium">
                    Selected
                  </div>
                )}
              </div>
            );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VisualizationStudio;

